import React, {
  useContext,
  useState,
  useEffect,
  Fragment,
  useRef
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'

import { ProductsDataContext } from '../../contexts/productsData.context'
import { StepHandlerContext } from '../../contexts/stepHandler.context'
import { NextButtonAvailabilityContext } from '../../contexts/nextButtonAvailability.context'
import { windowAmplitudeTrack } from '../../../shared/helpers/utils'

import {
  setSubscriptionMix,
  setSubscriptionStep,
  setSubscriptionUpsells
} from '../../store/subscription/subscription.action'
import {
  selectSubscriptionData,
  selectSubscriptionMix
} from '../../store/subscription/subscription.selector'
import { addBoxToCart, addUpsellsToCart } from '../../utils/cart/cart.utils'
import {
  SHOP_ROOT_PATH,
  PATTERN_STEP,
  UPSELLS_DATA
} from '../../utils/bab/bab.utils'

import Title from '../../components/title/title.component'
import UpsellGrid from '../../components/upsell-grid/upsell-grid.component'
import Text from '../../components/text/text.component'

import './upsell.styles.scss'

const Upsell = () => {
  const { t } = useTranslation('updatedBAB')
  const dispatch = useDispatch()
  const history = useHistory()

  const isFirstRender = useRef(true)
  const [localUpgrades, setLocalUpgrades] = useState([])
  const localUpgradesRef = useRef(localUpgrades)
  const [localUpsells, setLocalUpsells] = useState([])
  const localUpsellsRef = useRef(localUpsells)

  const productsData = useContext(ProductsDataContext)
  const { setPreviousStep, setNextStep } = useContext(StepHandlerContext)
  const { nextButtonAvailability, setNextButtonAvailability } = useContext(
    NextButtonAvailabilityContext
  )

  const subscriptionData = useSelector(selectSubscriptionData)
  const subscriptionMix = useSelector(selectSubscriptionMix)

  const upsellsData = UPSELLS_DATA

  // Cycle de vie composant
  useEffect(() => {
    const previousAvailability = nextButtonAvailability
    setPreviousStep(() => handlePreviousStep)
    setNextStep(() => handleNextStep)
    setNextButtonAvailability(true)

    return () => {
      setLocalUpgrades([])
      setLocalUpsells([])
      setPreviousStep(() => {})
      setNextStep(() => {})
      setNextButtonAvailability(previousAvailability)
    }
  }, [])

  // Mise à jour état local
  useEffect(() => {
    localUpgradesRef.current = localUpgrades
    localUpsellsRef.current = localUpsells

    dispatch(setSubscriptionUpsells(localUpgrades, localUpsells))
  }, [localUpgrades, localUpsells])

  const handlePreviousStep = () => {
    dispatch(setSubscriptionStep(2))
    history.replace(PATTERN_STEP.path)
  }

  const handleNextStep = () => {
    windowAmplitudeTrack('BabDone')
    dispatch(setSubscriptionMix(!subscriptionMix))
  }

  useEffect(() => {
    if (!isFirstRender.current) {
      const updateCart = async () => {
        await addBoxToCart(subscriptionData, productsData, t)
        await addUpsellsToCart(subscriptionData, productsData, t)
        window.open(`${SHOP_ROOT_PATH}?withCart`, '_self')
      }

      updateCart()
    } else {
      isFirstRender.current = false
    }
  }, [subscriptionMix])

  const handleUpsellChange = (event) => {
    const targetElement = event.target
    const container = targetElement.closest('.upsell-selector')
    const bundleUpsellClick = targetElement.closest('.upsell-subscription')
    const oneShotUpsellClick = targetElement.closest('.upsell-one-shot')
    const upsellCheckbox = targetElement.closest('.upsell-checkbox')

    if (!upsellCheckbox) return false

    const upgradeSku = container.dataset.upgradeSku
    const upsellSku = container.dataset.upsellSku

    const bundleUpsellContainer = container.querySelector(
      '.upsell-subscription'
    )
    const oneShotUpsellContainer = container.querySelector('.upsell-one-shot')

    const checkedBundleUpsell =
      bundleUpsellContainer.hasAttribute('data-checked')
    const checkedOneShotUpsell =
      oneShotUpsellContainer.hasAttribute('data-checked')

    if (!checkedBundleUpsell && !checkedOneShotUpsell) {
      if (bundleUpsellClick) {
        bundleUpsellContainer.setAttribute('data-checked', '')
        setLocalUpgrades([...localUpgrades, upgradeSku])
      } else if (oneShotUpsellClick) {
        oneShotUpsellContainer.setAttribute('data-checked', '')
        setLocalUpsells([...localUpsells, upsellSku])
      }
    } else if (checkedBundleUpsell) {
      if (bundleUpsellClick) {
        bundleUpsellContainer.removeAttribute('data-checked')
        setLocalUpgrades(localUpgrades.filter((sku) => sku !== upgradeSku))
      } else if (oneShotUpsellClick) {
        bundleUpsellContainer.removeAttribute('data-checked')
        setLocalUpgrades(localUpgrades.filter((sku) => sku !== upgradeSku))
        oneShotUpsellContainer.setAttribute('data-checked', '')
        setLocalUpsells([...localUpsells, upsellSku])
      }
    } else if (checkedOneShotUpsell) {
      if (bundleUpsellClick) {
        bundleUpsellContainer.setAttribute('data-checked', '')
        setLocalUpgrades([...localUpgrades, upgradeSku])
        oneShotUpsellContainer.removeAttribute('data-checked')
        setLocalUpsells(localUpsells.filter((sku) => sku !== upsellSku))
      } else if (oneShotUpsellClick) {
        oneShotUpsellContainer.removeAttribute('data-checked')
        setLocalUpsells(localUpsells.filter((sku) => sku !== upsellSku))
      }
    }
  }

  const changeUpsellVariantHandler = (event) => {
    const targetElement = event.target
    const container = targetElement.closest('.upsell-selector')
    const selectedOption = targetElement.selectedOptions[0]

    const upgradeSku = container.dataset.upgradeSku
    const newUpgradeSku = selectedOption.dataset.upgradeSku
    container.setAttribute('data-upgrade-sku', newUpgradeSku)

    const upsellSku = container.dataset.upsellSku
    const newUpsellSku = selectedOption.dataset.upsellSku
    container.setAttribute('data-upsell-sku', newUpsellSku)

    if (localUpgrades.includes(upgradeSku)) {
      const newUpgrades = localUpgrades.map((upsell) => {
        if (upsell === upgradeSku) {
          return newUpgradeSku
        } else {
          return upsell
        }
      })

      setLocalUpgrades(newUpgrades)
    } else if (localUpsells.includes(upsellSku)) {
      const newUpsells = localUpsells.map((upsell) => {
        if (upsell === upsellSku) {
          return newUpsellSku
        } else {
          return upsell
        }
      })

      setLocalUpsells(newUpsells)
    }
  }

  return (
    <Fragment>
      <Helmet>
        <title>{t('upsell.metaData.title')}</title>
        <meta name='description' content={t('upsell.metaData.description')} />
      </Helmet>
      <Title content={t('upsell.title', { returnObjects: true })} />
      <UpsellGrid
        upsellsData={upsellsData}
        margins={[32, 0, 32, 0]}
        clickHandler={handleUpsellChange}
        changeHandler={changeUpsellVariantHandler}
      />
      <Text
        content={t('upsell.note')}
        classNames={'upsell-note'}
        margins={[24, 0, 24, 0]}
      />
    </Fragment>
  )
}

export default Upsell
